import { Injectable } from '@angular/core';

import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { EventSubscription } from '@frontend/sports/event-subscription/feature/base-subscription';
import { GridSubscriptionService } from '@frontend/sports/event-subscription/feature/grid-subscription';
import { DetailedGridSubscriptionService } from '@frontend/sports/grid/core/feature/detailed-grid-subscription';
import { GridModel, SubscriptionTopic } from '@frontend/sports/grid/core/feature/model';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { tap } from 'rxjs';

import { GridStoreService } from './grid-store.service';
import { GridActions } from './grid.actions';
import { getEvents } from './grid.reducer';
import { IGridRootState, gridStateSelector } from './grid.state';

@Injectable({ providedIn: 'root' })
export class GridEffects {
    private subscriptions = new Map<string, EventSubscription<EventModel>>();

    constructor(
        private actions$: Actions,
        private store: Store<IGridRootState>,
        private subscriptionService: GridSubscriptionService,
        private detailedSubscriptionService: DetailedGridSubscriptionService,
        private gridStore: GridStoreService,
    ) {}

    onAddEvents$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(GridActions.addEvents),
                concatLatestFrom(() => this.store.select(gridStateSelector)),
                tap(([current, currentState]) => {
                    const grid = currentState[current.payload.id];

                    this.subscribe(grid, current.payload.events);
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    onSetEvents$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(GridActions.setEvents),
                concatLatestFrom(() => this.store.select(gridStateSelector)),
                tap(([current, currentState]) => {
                    const grid = currentState[current.payload.id];
                    this.unsubscribeGrid(grid.id);
                    this.subscribe(grid, current.payload.events);
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    onInit$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(GridActions.init),
                tap((current) => {
                    if (current.payload.groups) {
                        const events = getEvents(current.payload);
                        this.subscribe(current.payload, events);
                    }
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    onDestroy$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(GridActions.destroy),
                tap((current) => {
                    this.unsubscribeGrid(current.gridId);
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    collapseEvents$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(GridActions.collapseEvents),
                concatLatestFrom(() => this.store.select(gridStateSelector)),
                tap(([action, currentState]) => {
                    const grid = currentState[action.payload.id];
                    this.gridStore.set(grid);
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    onReplaceEvent$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(GridActions.replaceEvent),
                concatLatestFrom(() => this.store.select(gridStateSelector)),
                tap(([current, currentState]) => {
                    const grid = currentState[current.payload.id];

                    this.unsubscribeEvents(current.payload.id, [current.payload.targetFixtureId]);
                    this.subscribe(grid, current.payload.newFixture);
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    onRemoveEvents$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(GridActions.removeEvents),
                tap((current) => {
                    this.unsubscribeEvents(current.payload.id, current.payload.eventIds);
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    onResubscribe$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(GridActions.replaceEvents),
                concatLatestFrom(() => this.store.select(gridStateSelector)),
                tap(([current, currentState]) => {
                    const grid = currentState[current.payload.id];
                    const events = getEvents(grid);
                    this.unsubscribeGrid(grid.id);
                    this.subscribe(grid, events);
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    setGroupCollapse$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(GridActions.setGroupCollapse),
                concatLatestFrom(() => this.store.select(gridStateSelector)),
                tap(([action, currentState]) => {
                    const grid = currentState[action.payload.id];
                    this.gridStore.set(grid, action.payload.groupId);
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    setActiveGroup$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(GridActions.setActiveGroup),
                concatLatestFrom(() => this.store.select(gridStateSelector)),
                tap(([action, currentState]) => {
                    const grid = currentState[action.payload.id];
                    this.gridStore.set(grid);
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    private subscribe(grid: GridModel, events: EventModel | EventModel[]): void {
        const collection = Array.isArray(events) ? events : [events];
        const request = collection.map((event: EventModel) => ({
            event,
            callback: (current: EventModel) => {
                this.updateEvent(grid.id, current);
            },
        }));

        const subscription = this.gridableView(grid)
            ? this.subscriptionService.subscribe(request)
            : this.detailedSubscriptionService.subscribe(request, grid.topic, grid.topicDetail);

        const existing = this.subscriptions.get(grid.id);

        if (existing) {
            this.subscriptions.set(grid.id, existing.add(subscription));
        } else {
            this.subscriptions.set(grid.id, subscription);
        }
    }

    private gridableView(grid: GridModel): boolean {
        return ![
            SubscriptionTopic.Outrights,
            SubscriptionTopic.Specials,
            SubscriptionTopic.NonGridable,
            SubscriptionTopic.All,
            SubscriptionTopic.DynamicCategory,
        ].includes(grid.topic);
    }

    private unsubscribeGrid(gridId: string): void {
        const subscription = this.subscriptions.get(gridId);

        if (!subscription) {
            return;
        }

        subscription.unsubscribe();
        this.subscriptions.delete(gridId);
    }

    private unsubscribeEvents(gridId: string, events: (EventModel | string)[]): void {
        const subscription = this.subscriptions.get(gridId);

        if (!subscription) {
            return;
        }

        subscription.remove(events);
    }

    private updateEvent(id: string, event: EventModel): void {
        this.store.dispatch(GridActions.updateEvents({ payload: { id, event } }));
    }
}
